import { createActions } from 'redux-actions';
import { produce } from 'immer';

import { actionTypes } from './actionTypes';
import { sortSectionStatus } from '@modules/questionnaires/useQuestionSteps';
import { API } from '@service';
import { formatComplexityData } from '@modules/questionnaires/complexity/sections/overall/overallUtils';
import {
  formatReqData,
  formatStrategyData,
  formatReqRiskData,
  formatOverallComplexityResponse,
  formatOverallComplexityRequestBody,
  getLevelStringByPercent,
} from '@modules/questionnaires/complexity/sections/strategy/strategyComplexyUtils';

import commonActions from './common';
import {
  quesModel,
  QUES_SECTIONS_SEQ,
  SECTIONS_LENGTH,
  QUESTION_STATUS,
} from '@model/questionmaires/questionnaire';

const {
  questionnaires: {
    CHANGE_QUESTIONS,
    CLEAR_QUESTIONS,
    QUESTIONNAIRE_ACCESS_HANDLER,
    CHANGE_COMPLEXITY_MODE,
    CHANGE_SIDEBAR_SECTION_LEVEL,
    CANCEL_QUESTION_EDIT,
  },
} = actionTypes;

const questionActions = createActions(
  {},
  CHANGE_QUESTIONS,
  CLEAR_QUESTIONS,
  QUESTIONNAIRE_ACCESS_HANDLER,
  CHANGE_COMPLEXITY_MODE,
  CHANGE_SIDEBAR_SECTION_LEVEL,
  CANCEL_QUESTION_EDIT,
);

const changeTargetStepStatusAndFetch =
  ({ section, stepIndex, actualIndex }) =>
  async (dispatch, getState) => {
    const {
      question: { status: currentStatus },
      common: { currentId: projectId },
    } = getState();
    //update local state first

    const newStatus = produce(currentStatus, (draft) => {
      if (draft[section]?.length > -1) {
        if (draft[section][stepIndex] === 0) {
          draft[section][stepIndex] = 1;
        }
      }
    });

    dispatch(
      questionActions.changeQuestions({
        status: newStatus,
        isFetchLoading: true,
      }),
    );

    try {
      const { attributes = {} } =
        (await API.getQuestionCollectionRequest({
          projectId,
          sequence: actualIndex + 1,
        })) || {};
      const syncedStatus = attributes.questionnaire?.status
        ? sortSectionStatus(attributes.questionnaire.status)
        : newStatus;

      dispatch(
        questionActions.changeQuestions({
          status: syncedStatus,
          currentQuesAttributes: attributes,
          isFetchLoading: false,
        }),
      );
    } catch (e) {
      dispatch(
        questionActions.changeQuestions({
          isFetchLoading: false,
        }),
      );
    }
  };

const fetchQuesCollections = () => async (dispatch, getState) => {
  const projectId = getState().common.currentId;
  dispatch(
    questionActions.changeQuestions({
      isFetchLoading: true,
    }),
  );

  try {
    const result =
      (await API.getQuestionCollectionRequest({
        projectId,
        queryType: 'status',
      })) || {};

    const payload = {};

    if (result?.members) {
      payload.status = sortSectionStatus(result.members);
    }

    if (result?.attributes) {
      payload.attributes = result.attributes;
    }

    dispatch(
      questionActions.changeQuestions({ ...payload, isFetchLoading: false }),
    );
  } catch (e) {
    dispatch(questionActions.changeQuestions({ isFetchLoading: false }));
  }
};

// const isAnswerChanged = (oldAnswer = {}, newAnswer = {}) =>
//   JSON.stringify(oldAnswer) !== JSON.stringify(newAnswer);

const submitQuestionAnswer =
  ({ index, values, isLast, section, shouldAnswerRequest = false }) =>
  async (dispatch, getState) => {
    const {
      common: { currentId: projectId },
      question: {
        currentQuesAttributes: {
          questionStem: { sequenceNumber },
          questionnaire: { status: statusArray },
        },
        status,
      },
    } = getState();
    let result = {
      success: true,
    };
    const currentStatus = statusArray[sequenceNumber - 1];
    dispatch(questionActions.changeQuestions({ isActionLoading: true }));

    try {
      if (currentStatus !== 3 && shouldAnswerRequest === true) {
        result = await API.submitQuestionAnswerRequest({
          projectId,
          sequence: ++index,
          answer: values,
        });
      }

      if (isLast) {
        const { attributes = {} } =
          (await API.getQuestionCollectionRequest({
            projectId,
            sequence: SECTIONS_LENGTH.reduce(
              (previous, current) => previous + current,
              0,
            ),
          })) || {};

        const payload = { attributes };

        if (attributes.questionnaire?.status) {
          payload.status = sortSectionStatus(attributes.questionnaire.status);
        }

        dispatch(questionActions.changeQuestions(payload));
      }

      const isSectionHasOneQuestion =
        status[section].filter(
          (s) =>
            s !== QUESTION_STATUS.Finish && s !== QUESTION_STATUS.NotApplicable,
        ).length <= 1;

      if (isSectionHasOneQuestion) {
        await API.submitReviewResponses(projectId);
      }

      dispatch(getComplexitySiderData(projectId));

      return result || {};
    } catch (e) {
      return { success: false };
    } finally {
      dispatch(questionActions.changeQuestions({ isActionLoading: false }));
    }
  };

const changeToNotApplicable =
  ({ actualIndex }) =>
  async (dispatch, getState) => {
    const {
      common: { currentId: projectId },
    } = getState();
    try {
      await API.hideQuestion(projectId, actualIndex);
    } catch (e) {
      // Global REST feedback already ran.
    }
  };

const fetchComplexityMode = () => async (dispatch, getState) => {
  const {
    common: { currentId: projectId },
  } = getState();
  try {
    const data = await API.fetchComplexityMode(projectId);
    const isManual = data.complexityOutputManualViewFlag ? 'manual' : 'auto';

    dispatch(questionActions.changeComplexityMode(isManual));
  } catch (e) {
    // Global REST feedback already ran; avoid unhandled rejection.
  }
};

const postComplexityMode =
  ({ projectStatusInfo }) =>
  async (dispatch, getState) => {
    const {
      common: { currentId: projectId },
    } = getState();
    API.postComplexityMode(projectId, { projectStatusInfo });
    // dispatch(questionActions.changeComplexityMode(data));
  };

const fetchComplexityBySection =
  (section, isManual) => async (dispatch, getState) => {
    const {
      common: { currentId: projectId },
    } = getState();
    dispatch(
      questionActions.changeQuestions({
        isFetchLoading: true,
      }),
    );

    try {
      let isManualMode;
      if (isManual === undefined) {
        const modeData = await API.fetchComplexityMode(projectId);
        isManualMode = modeData.complexityOutputManualViewFlag;
      } else {
        isManualMode = isManual;
      }
      const data = await API.getComplexityBySectionRequest({
        projectId,
        section,
        isManualMode,
      });

      dispatch(getComplexitySiderData(projectId));

      const payload = {
        isFetchLoading: false,
        complexityData: data,
        [`${section}Stock`]: data,
        allComplexityData: {},
      };

      if (section === 'overall') {
        payload.complexityData = formatOverallComplexityResponse(data);
        payload.lastEditAt = new Date(data?.lastAdjustedAt);
        payload.lastEditBy = data?.lastEditedBy;
      } else {
        const formatData = formatStrategyData(data, quesModel[section]);
        payload.complexityData = formatData?.data;
        payload.modelsData = formatData?.models;
        payload.allComplexityData.section = payload.complexityData;
      }

      dispatch(questionActions.changeQuestions(payload));
    } catch (e) {
      dispatch(questionActions.changeQuestions({ isFetchLoading: false }));
    }
  };

const fetchComplexityByPdf = (section) => async (dispatch, getState) => {
  const {
    common: { currentId: projectId },
    question: { allComplexityData, complexitySiderData = {} },
  } = getState();
  let complexities = complexitySiderData.complexities;
  dispatch(
    questionActions.changeQuestions({
      isFetchLoading: true,
    }),
  );

  try {
    if (!complexities) {
      const { abled, ...siderData } = await API.getComplexitySiderDataRequest(
        projectId,
      );
      complexities = siderData;
    }

    const restSections = [...Object.keys(complexities), 'overall'].reduce(
      (res, cur) => {
        if (!Object.keys(allComplexityData).find((item) => item === cur)) {
          res.push(cur);
        }
        return res;
      },
      [],
    );
    const allData = {};
    let lastEditAt;
    let lastEditBy;

    const arr = restSections.map((item) =>
      API.getComplexityBySectionRequest({ projectId, section: item }),
    );

    const pdfData = await Promise.all([...arr]);

    restSections.forEach((sec, i) => {
      if (sec === 'overall') {
        allData[sec] = formatComplexityData(pdfData[i]);
        lastEditAt = new Date(pdfData[i]?.lastAdjustedAt);
        lastEditBy = pdfData[i]?.lastEditedBy;
      } else {
        allData[sec] = pdfData[i];
        allData[sec] = formatStrategyData(pdfData[i], quesModel[sec])?.data;
      }
    });

    const payload = {
      isFetchLoading: false,
      allComplexityData: { ...allComplexityData, ...allData },
      lastEditAt,
      lastEditBy,
    };

    dispatch(questionActions.changeQuestions(payload));
  } catch (e) {
    dispatch(questionActions.changeQuestions({ isFetchLoading: false }));
  }
};

const saveComplexityBySection =
  ({ section, formData }) =>
  async (dispatch) => {
    dispatch(questionActions.changeQuestions({ isActionLoading: true }));

    let result = {};
    try {
      if (section === 'overall') {
        result = await dispatch(
          saveOverallComplexity({ data: formData, section }),
        );
      } else {
        result = await dispatch(
          saveStrategyComplexity({ data: formData, section }),
        );
      }

      if (result?.success) {
        dispatch(fetchComplexityBySection(section));
      }

      return result;
    } finally {
      dispatch(questionActions.changeQuestions({ isActionLoading: false }));
    }
  };

const saveRiskBySection =
  ({ section, formData }) =>
  async (dispatch) => {
    dispatch(questionActions.changeQuestions({ isRiskLoading: true }));
    let result = {};
    try {
      result = await dispatch(saveRisk({ data: formData, section }));
      if (result?.success) {
        dispatch(fetchComplexityBySection(section));
      }
      return result;
    } finally {
      dispatch(questionActions.changeQuestions({ isRiskLoading: false }));
    }
  };

const saveStrategyComplexity =
  ({ data, section }) =>
  async (dispatch, getState) => {
    const {
      question: { currentComplexityMode },
      common: { currentId: projectId },
      question,
    } = getState();
    const originData = question[`${section}Stock`];
    const reqData = formatReqData(
      originData,
      data,
      section,
      quesModel[section],
    );
    const isManual = currentComplexityMode === 'manual';

    try {
      const result = await API.postComplexityBySectionRequest({
        projectId,
        section,
        reqData,
        isManual,
      });

      return { success: result?.message.includes('success') };
    } catch (e) {
      return { success: false };
    }
  };

const saveRisk =
  ({ data, section }) =>
  async (dispatch, getState) => {
    const {
      //  : data,
      question: { currentComplexityMode },
      common: { currentId: projectId },
    } = getState();
    // const originData = question[`${section}Stock`];
    const reqData = formatReqRiskData(data, section);
    const isManual = currentComplexityMode === 'manual';
    try {
      const result = await API.postRiskRequest({
        projectId,
        section,
        reqData,
        isManual,
      });

      return { success: result?.message.includes('success') };
    } catch (e) {
      return { success: false };
    }
  };

const replaceWithManualData = (data, manualData) => {
  const sectionComplexities = manualData?.sectionComplexity || [];
  const assigneeData = sectionComplexities.reduce(
    (acc, e) =>
      Object.assign(acc, {
        [`section-level-${e.seqNum}`]: e.levelPercent,
      }),
    { level: manualData?.overallComplexity?.levelPercent },
  );

  return Object.assign({}, data, assigneeData);
};

const saveOverallComplexity =
  ({ data }) =>
  async (dispatch, getState) => {
    const {
      question: { currentComplexityMode },
      common: { currentId: projectId },
    } = getState();

    let dataToPost = data;

    const isManual = currentComplexityMode === 'manual';

    try {
      if (!isManual) {
        const manualData = await API.getComplexityBySectionRequest({
          projectId,
          section: 'overall',
          isManualMode: true,
        });

        dataToPost = replaceWithManualData(dataToPost, manualData);
      }

      const result = await API.submitOverallComplexityTextRequest({
        projectId,
        postBody: formatOverallComplexityRequestBody(dataToPost),
        isManual: true,
      });

      return { success: result?.message.includes('success') };
    } catch (e) {
      return { success: false };
    }
  };

const getComplexitySiderData = (projectId) => async (dispatch, getState) => {
  const currentId = projectId || getState().common.currentId;
  let abled;
  let complexitySiderData;
  try {
    const res = await API.getComplexitySiderDataRequest(currentId);
    ({ abled, ...complexitySiderData } = res);
  } catch (e) {
    return;
  }

  const keys = Object.keys(complexitySiderData);
  const clickableSectionPaths = ['overall', ...QUES_SECTIONS_SEQ].reduce(
    (result, item) => {
      keys.includes(item) && result.push(item === 'overall' ? '' : `/${item}`);
      return result;
    },
    [],
  );

  if (clickableSectionPaths.length > 0 && clickableSectionPaths[0] !== '') {
    clickableSectionPaths.unshift('');
  }

  dispatch(
    questionActions.changeQuestions({
      complexitySiderData: {
        abled,
        clickableSectionPaths,
        complexities: complexitySiderData,
      },
    }),
  );
};

const resetSection = (sectionSeq) => async (dispatch, getState) => {
  const {
    common: { currentId: projectId },
  } = getState();
  try {
    const result = await API.resetSection(projectId, sectionSeq);
    dispatch(getComplexitySiderData(projectId));
    dispatch(
      questionActions.changeQuestions({
        complexitySiderData: result,
      }),
    );
  } catch (e) {
    // Global REST feedback already ran.
  }
};

const getPDFCustomization = () => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));
  const projectId = getState().common.currentId;
  try {
    let response = await API.getPDFCustomizationSettings(projectId);

    if (response.data === null) {
      response = {};
    }
    await dispatch(
      questionActions.changeQuestions({
        pdfCustomizationSettings: response,
      }),
    );
    return { success: true };
  } catch (e) {
    return { success: false };
  } finally {
    await dispatch(commonActions.changeLoading(false));
  }
};
const savePDFCustomization = (settings) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));

  const projectId = getState().common.currentId;

  try {
    const response = await API.savePDFCustomizationSettings(
      projectId,
      settings,
    );

    if (response) {
      dispatch(
        questionActions.changeQuestions({
          pdfCustomizationSettings: settings,
        }),
      );
    }

    return { success: true };
  } catch (e) {
    return { success: false };
  } finally {
    dispatch(commonActions.changeLoading(false));
  }
};

const changeSidebarSectionLevel = (payload) => (dispatch, getState) => {
  const complexitySiderData = getState().question.complexitySiderData;
  const originInfo = complexitySiderData.complexities[payload.section];

  complexitySiderData.complexities[payload.section] = {
    originLevel: originInfo.originLevel || originInfo.level,
    originLevelPercent:
      originInfo.originLevelPercent || originInfo.levelPercent,
    levelPercent: parseInt((payload.levelPercent || 0) * 100),
    level: getLevelStringByPercent(payload.levelPercent || 0),
  };

  dispatch(
    questionActions.changeQuestions({
      complexitySiderData,
    }),
  );
};

const cancelQuestionEdit = () => (dispatch, getState) => {
  const complexitySiderData = getState().question.complexitySiderData;
  Object.keys(complexitySiderData.complexities).forEach((section) => {
    const sectionInfo = {
      ...(complexitySiderData.complexities[section] || {}),
    };
    if (sectionInfo.originLevel || sectionInfo.originLevelPercent) {
      complexitySiderData.complexities[section] = {
        level: sectionInfo.originLevel,
        levelPercent: sectionInfo.originLevelPercent,
      };
    }
  });
  dispatch(
    questionActions.changeQuestions({
      complexitySiderData,
    }),
  );
};

export default {
  ...questionActions,
  changeTargetStepStatusAndFetch,
  fetchQuesCollections,
  submitQuestionAnswer,
  changeToNotApplicable,
  fetchComplexityBySection,
  saveOverallComplexity,
  saveComplexityBySection,
  getComplexitySiderData,
  saveRiskBySection,
  resetSection,
  fetchComplexityByPdf,
  getPDFCustomization,
  savePDFCustomization,
  fetchComplexityMode,
  postComplexityMode,
  changeSidebarSectionLevel,
  cancelQuestionEdit,
};
